/**
 * Toaster — site-wide notification surface.
 *
 * Visual is locked to the app's design system (Notion-tone): card-paper
 * background, hairline border, shadow-md, JetBrains-mono caption for
 * the optional description. We deliberately avoid Sonner's `richColors`
 * mode (loud green/red fills) — semantic tone is signalled by a small
 * left-edge accent on the icon column instead, so toasts read like the
 * canvas's own callouts.
 */

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, type ToasterProps } from "sonner";

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4 text-emerald-600 dark:text-emerald-400" />,
        info: <InfoIcon className="size-4 text-blue-600 dark:text-blue-400" />,
        warning: <TriangleAlertIcon className="size-4 text-amber-600 dark:text-amber-400" />,
        error: <OctagonXIcon className="size-4 text-destructive" />,
        loading: <Loader2Icon className="size-4 animate-spin text-primary" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          // Park the close button inside the toast at the top-right (not
          // overlapping the rounded corner the way sonner's default does).
          "--toast-close-button-start": "auto",
          "--toast-close-button-end": "8px",
          "--toast-close-button-transform": "translate(0, 8px)",
        } as React.CSSProperties
      }
      toastOptions={{
        unstyled: false,
        closeButton: true,
        classNames: {
          // pr-9 leaves room for the close button so the title never
          // gets clipped by the X.
          toast:
            "cn-toast group !bg-popover !text-popover-foreground !border-border !shadow-md !rounded-lg !py-3 !pl-3 !pr-9 !text-[13px] !font-sans !min-w-[280px] !max-w-[420px]",
          title: "!text-[13px] !font-medium !leading-tight !tracking-tight",
          description: "!text-[12px] !text-muted-foreground !leading-snug !mt-0.5",
          actionButton:
            "!bg-primary !text-primary-foreground !rounded-md !px-2.5 !py-1 !text-[12px] !font-medium",
          cancelButton:
            "!bg-transparent !text-muted-foreground hover:!text-foreground !rounded-md !px-2.5 !py-1 !text-[12px] !font-medium",
          closeButton:
            "!size-5 !rounded-md !bg-transparent !border-transparent !text-muted-foreground/70 hover:!bg-muted hover:!text-foreground !transition-colors !opacity-0 group-hover:!opacity-100 focus-visible:!opacity-100 [&_svg]:!size-3",
          icon: "!size-4",
          success: "[--toast-icon-margin-end:0.625rem]",
          info: "[--toast-icon-margin-end:0.625rem]",
          warning: "[--toast-icon-margin-end:0.625rem]",
          error: "[--toast-icon-margin-end:0.625rem]",
          loading: "[--toast-icon-margin-end:0.625rem]",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
